// @ts-nocheck
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Box } from '@/components/ui';

export type IllustrationType =
  | 'login'
  | 'register'
  | 'forgot-password'
  | 'reset-password'
  | 'otp'
  | 'security'
  | 'success'
  | 'email';

interface AuthIllustrationProps {
  type: IllustrationType;
  size?: number;
  animated?: boolean;
}

export default function AuthIllustration({
  type,
  size = 200,
  animated = true,
}: AuthIllustrationProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const primaryColor = theme.colors.indigo[isDark ? 4 : 6];
  const secondaryColor = theme.colors.cyan[isDark ? 4 : 5];
  const accentColor = theme.colors.violet[isDark ? 4 : 5];
  const bgColor = isDark ? theme.colors.dark[6] : theme.colors.gray[1];

  const animationStyle = animated ? {
    animation: 'float 3s ease-in-out infinite',
  } : {};

  // Login illustration - Person with key
  if (type === 'login') {
    return (
      <Box component="svg" viewBox="0 0 200 200" width={size} height={size} style={animationStyle}>
        <defs>
          <linearGradient id="loginGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={primaryColor} />
            <stop offset="100%" stopColor={secondaryColor} />
          </linearGradient>
        </defs>
        {/* Background circle */}
        <circle cx="100" cy="100" r="80" fill={bgColor} opacity="0.5" />
        {/* Lock body */}
        <rect x="70" y="90" width="60" height="50" rx="8" fill="url(#loginGrad)" />
        {/* Lock shackle */}
        <path
          d="M80 90 L80 70 A20 20 0 0 1 120 70 L120 90"
          fill="none"
          stroke={primaryColor}
          strokeWidth="8"
          strokeLinecap="round"
        />
        {/* Keyhole */}
        <circle cx="100" cy="110" r="8" fill={isDark ? theme.colors.dark[9] : 'white'} />
        <rect x="96" y="110" width="8" height="15" rx="2" fill={isDark ? theme.colors.dark[9] : 'white'} />
        {/* Decorative dots */}
        <circle cx="45" cy="60" r="5" fill={accentColor} opacity="0.6" />
        <circle cx="155" cy="140" r="4" fill={secondaryColor} opacity="0.6" />
        <circle cx="40" cy="130" r="3" fill={primaryColor} opacity="0.4" />
      </Box>
    );
  }

  // Register illustration - User with plus
  if (type === 'register') {
    return (
      <Box component="svg" viewBox="0 0 200 200" width={size} height={size} style={animationStyle}>
        <defs>
          <linearGradient id="registerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={secondaryColor} />
            <stop offset="100%" stopColor={primaryColor} />
          </linearGradient>
        </defs>
        {/* Background circle */}
        <circle cx="100" cy="100" r="80" fill={bgColor} opacity="0.5" />
        {/* User head */}
        <circle cx="100" cy="75" r="25" fill="url(#registerGrad)" />
        {/* User body */}
        <path
          d="M60 140 Q60 110 100 110 Q140 110 140 140"
          fill="url(#registerGrad)"
        />
        {/* Plus sign */}
        <circle cx="145" cy="55" r="20" fill={accentColor} />
        <rect x="140" y="45" width="10" height="20" rx="2" fill="white" />
        <rect x="135" y="50" width="20" height="10" rx="2" fill="white" />
        {/* Decorative elements */}
        <circle cx="40" cy="80" r="4" fill={secondaryColor} opacity="0.5" />
        <circle cx="160" cy="130" r="3" fill={primaryColor} opacity="0.5" />
      </Box>
    );
  }

  // Forgot password illustration - Question mark with lock
  if (type === 'forgot-password') {
    return (
      <Box component="svg" viewBox="0 0 200 200" width={size} height={size} style={animationStyle}>
        <defs>
          <linearGradient id="forgotGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={theme.colors.orange[5]} />
            <stop offset="100%" stopColor={theme.colors.yellow[5]} />
          </linearGradient>
        </defs>
        {/* Background circle */}
        <circle cx="100" cy="100" r="80" fill={bgColor} opacity="0.5" />
        {/* Question mark */}
        <path
          d="M85 70 Q85 50 100 50 Q120 50 120 70 Q120 85 100 90"
          fill="none"
          stroke="url(#forgotGrad)"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <circle cx="100" cy="115" r="8" fill="url(#forgotGrad)" />
        {/* Small lock */}
        <rect x="130" y="120" width="30" height="25" rx="4" fill={primaryColor} />
        <path
          d="M136 120 L136 110 A9 9 0 0 1 154 110 L154 120"
          fill="none"
          stroke={primaryColor}
          strokeWidth="4"
          strokeLinecap="round"
        />
        {/* Decorative */}
        <circle cx="50" cy="70" r="4" fill={accentColor} opacity="0.5" />
        <circle cx="160" cy="80" r="3" fill={secondaryColor} opacity="0.5" />
      </Box>
    );
  }

  // Reset password illustration - Key with circular arrows
  if (type === 'reset-password') {
    return (
      <Box component="svg" viewBox="0 0 200 200" width={size} height={size} style={animationStyle}>
        <defs>
          <linearGradient id="resetGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={theme.colors.green[5]} />
            <stop offset="100%" stopColor={theme.colors.teal[5]} />
          </linearGradient>
        </defs>
        {/* Background circle */}
        <circle cx="100" cy="100" r="80" fill={bgColor} opacity="0.5" />
        {/* Key */}
        <circle cx="75" cy="85" r="20" fill="none" stroke="url(#resetGrad)" strokeWidth="8" />
        <rect x="90" y="80" width="45" height="10" rx="3" fill="url(#resetGrad)" />
        <rect x="120" y="80" width="8" height="20" rx="2" fill="url(#resetGrad)" />
        <rect x="108" y="80" width="8" height="15" rx="2" fill="url(#resetGrad)" />
        {/* Circular arrow */}
        <path
          d="M130 140 A30 30 0 1 1 160 110"
          fill="none"
          stroke={primaryColor}
          strokeWidth="6"
          strokeLinecap="round"
        />
        <polygon points="160,100 170,110 155,115" fill={primaryColor} />
        {/* Decorative */}
        <circle cx="45" cy="130" r="4" fill={accentColor} opacity="0.5" />
      </Box>
    );
  }

  // OTP illustration - Phone with code
  if (type === 'otp') {
    return (
      <Box component="svg" viewBox="0 0 200 200" width={size} height={size} style={animationStyle}>
        <defs>
          <linearGradient id="otpGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={accentColor} />
            <stop offset="100%" stopColor={primaryColor} />
          </linearGradient>
        </defs>
        {/* Background circle */}
        <circle cx="100" cy="100" r="80" fill={bgColor} opacity="0.5" />
        {/* Phone */}
        <rect x="65" y="40" width="70" height="120" rx="12" fill="url(#otpGrad)" />
        <rect x="72" y="55" width="56" height="80" rx="4" fill={isDark ? theme.colors.dark[9] : 'white'} />
        {/* Code boxes */}
        <rect x="78" y="85" width="16" height="20" rx="3" fill={bgColor} stroke={primaryColor} strokeWidth="2" />
        <rect x="98" y="85" width="16" height="20" rx="3" fill={bgColor} stroke={primaryColor} strokeWidth="2" />
        <rect x="118" y="85" width="16" height="20" rx="3" fill={bgColor} stroke={primaryColor} strokeWidth="2" />
        {/* Signal waves */}
        <path d="M145 50 Q155 55 145 60" fill="none" stroke={secondaryColor} strokeWidth="3" strokeLinecap="round" />
        <path d="M150 45 Q165 55 150 65" fill="none" stroke={secondaryColor} strokeWidth="3" strokeLinecap="round" opacity="0.7" />
        {/* Decorative */}
        <circle cx="45" cy="75" r="4" fill={secondaryColor} opacity="0.5" />
      </Box>
    );
  }

  // Security illustration - Shield with checkmark
  if (type === 'security') {
    return (
      <Box component="svg" viewBox="0 0 200 200" width={size} height={size} style={animationStyle}>
        <defs>
          <linearGradient id="securityGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={theme.colors.teal[5]} />
            <stop offset="100%" stopColor={theme.colors.cyan[5]} />
          </linearGradient>
        </defs>
        {/* Background circle */}
        <circle cx="100" cy="100" r="80" fill={bgColor} opacity="0.5" />
        {/* Shield */}
        <path
          d="M100 35 L145 55 L145 100 Q145 145 100 165 Q55 145 55 100 L55 55 Z"
          fill="url(#securityGrad)"
        />
        {/* Checkmark */}
        <path
          d="M75 100 L92 117 L125 80"
          fill="none"
          stroke="white"
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Decorative */}
        <circle cx="160" cy="60" r="5" fill={accentColor} opacity="0.6" />
        <circle cx="40" cy="140" r="4" fill={primaryColor} opacity="0.5" />
      </Box>
    );
  }

  // Success illustration - Checkmark circle
  if (type === 'success') {
    return (
      <Box component="svg" viewBox="0 0 200 200" width={size} height={size} style={animationStyle}>
        <defs>
          <linearGradient id="successGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={theme.colors.green[5]} />
            <stop offset="100%" stopColor={theme.colors.teal[4]} />
          </linearGradient>
        </defs>
        {/* Background circle */}
        <circle cx="100" cy="100" r="80" fill={bgColor} opacity="0.5" />
        {/* Main circle */}
        <circle cx="100" cy="100" r="55" fill="url(#successGrad)" />
        {/* Checkmark */}
        <path
          d="M70 100 L90 120 L130 75"
          fill="none"
          stroke="white"
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Celebration dots */}
        <circle cx="45" cy="55" r="5" fill={theme.colors.yellow[5]} opacity="0.8" />
        <circle cx="155" cy="60" r="4" fill={accentColor} opacity="0.7" />
        <circle cx="160" cy="140" r="3" fill={secondaryColor} opacity="0.6" />
        <circle cx="40" cy="130" r="4" fill={primaryColor} opacity="0.5" />
      </Box>
    );
  }

  // Email illustration - Envelope
  if (type === 'email') {
    return (
      <Box component="svg" viewBox="0 0 200 200" width={size} height={size} style={animationStyle}>
        <defs>
          <linearGradient id="emailGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={theme.colors.blue[5]} />
            <stop offset="100%" stopColor={theme.colors.indigo[5]} />
          </linearGradient>
        </defs>
        {/* Background circle */}
        <circle cx="100" cy="100" r="80" fill={bgColor} opacity="0.5" />
        {/* Envelope body */}
        <rect x="40" y="65" width="120" height="80" rx="8" fill="url(#emailGrad)" />
        {/* Envelope flap */}
        <path
          d="M40 73 L100 115 L160 73"
          fill="none"
          stroke={isDark ? theme.colors.dark[9] : 'white'}
          strokeWidth="4"
          strokeLinejoin="round"
        />
        {/* At symbol */}
        <circle cx="145" cy="55" r="15" fill={accentColor} />
        <text x="145" y="61" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold">@</text>
        {/* Decorative */}
        <circle cx="50" cy="155" r="4" fill={secondaryColor} opacity="0.5" />
        <circle cx="155" cy="145" r="3" fill={primaryColor} opacity="0.5" />
      </Box>
    );
  }

  return null;
}
